import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon, type IconProps } from '@/components/ui/icon';
import { Calendar03Icon, Chart01Icon, CheckmarkBadge01Icon, UserMultiple02Icon } from '@/components/ui/icon/glyphs';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCount } from '@/lib/format/format-count';
import { formatShortDate } from '@/lib/format/format-date';
import { cn } from '@/lib/ui/utils';
import type { StatsVM } from './derive';
const UNKNOWN_DATE = '—';
interface StatStripProps {
  stats: StatsVM;
}
interface StatTileProps {
  statKey: 'consensus' | 'marketVolume' | 'participants' | 'resolvedAt';
  icon: IconProps['icon'];
  label: string;
  valueClassName?: string;
  /** Optional explanation shown on hover (mirrors the market-info cells). */
  tooltip?: ReactNode;
  children: ReactNode;
}
function StatTile(t0) {
  const $ = _c(22);
  const {
    statKey,
    icon,
    label,
    valueClassName,
    tooltip,
    children
  } = t0;
  const t1 = tooltip !== undefined && tooltip !== null && "cursor-help hover:bg-background-tertiary";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex min-w-0 items-center gap-3 rounded-[calc(var(--radius-card)-0.25rem)] bg-background-tertiary/50 px-4 py-3 transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon) {
    t3 = <Icon icon={icon} aria-hidden={true} className="size-6 shrink-0 text-foreground-tertiary" />;
    $[2] = icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <span className="truncate text-caption text-foreground-tertiary">{label}</span>;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== valueClassName) {
    t5 = cn("truncate text-body-lg font-medium tabular-nums text-foreground-secondary", valueClassName);
    $[6] = valueClassName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== children || $[9] !== t5) {
    t6 = <span className={t5}>{children}</span>;
    $[8] = children;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <div className="flex min-w-0 flex-col gap-0.5">{t4}{t6}</div>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== statKey || $[15] !== t2 || $[16] !== t3 || $[17] !== t7) {
    t8 = <div data-testid="resolved-panel-stat-tile" data-stat-key={statKey} className={t2}>{t3}{t7}</div>;
    $[14] = statKey;
    $[15] = t2;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const tile = t8;
  if (tooltip === undefined || tooltip === null) {
    return tile;
  }
  let t9;
  if ($[19] !== tile || $[20] !== tooltip) {
    t9 = <AppTooltip content={tooltip} side="top" align="start">{tile}</AppTooltip>;
    $[19] = tile;
    $[20] = tooltip;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}

/**
 * The 4-tile resolved stat strip (Figma 3928-816): Consensus (winner's final
 * consensus), Market volume (lifetime), Participants (lifetime), Resolved date.
 * 2-up on mobile, 4-up at sm+.
 */
export function StatStrip(t0) {
  const $ = _c(48);
  const {
    stats
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== formatD6 || $[1] !== stats.volumeTotalD6) {
    t1 = formatD6((stats.volumeTotalD6 ?? BigInt(0)).toString(), "compact");
    $[0] = formatD6;
    $[1] = stats.volumeTotalD6;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const volume = t1;
  const t2 = stats.uniqueTradersTotal ?? 0;
  let t3;
  if ($[3] !== locale || $[4] !== t2) {
    t3 = formatCount(t2, locale);
    $[3] = locale;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const participants = t3;
  let t4;
  if ($[6] !== locale || $[7] !== stats.resolvedAt) {
    t4 = stats.resolvedAt === null ? UNKNOWN_DATE : formatShortDate(stats.resolvedAt, locale);
    $[6] = locale;
    $[7] = stats.resolvedAt;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const resolvedAt = t4;
  let t5;
  if ($[9] !== t) {
    t5 = t("statConsensus");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("tooltipConsensus");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = stats.consensusPct ?? 0;
  let t8;
  if ($[13] !== t7) {
    t8 = <AnimatedOutcomePercent value={t7} decimals={false} />;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t8) {
    t9 = <StatTile statKey="consensus" icon={CheckmarkBadge01Icon} label={t5} valueClassName="text-feedback-success-content" tooltip={t6}>{t8}</StatTile>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("statMarketVolume");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("tooltipMarketVolume");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== volume) {
    t12 = <StatTile statKey="marketVolume" icon={Chart01Icon} label={t10} tooltip={t11}>{volume}</StatTile>;
    $[23] = t10;
    $[24] = t11;
    $[25] = volume;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("statParticipants");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("tooltipParticipants");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== participants || $[32] !== t13 || $[33] !== t14) {
    t15 = <StatTile statKey="participants" icon={UserMultiple02Icon} label={t13} tooltip={t14}>{participants}</StatTile>;
    $[31] = participants;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("statResolved");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("tooltipResolved");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== resolvedAt || $[40] !== t16 || $[41] !== t17) {
    t18 = <StatTile statKey="resolvedAt" icon={Calendar03Icon} label={t16} valueClassName="text-foreground-primary" tooltip={t17}>{resolvedAt}</StatTile>;
    $[39] = resolvedAt;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t12 || $[44] !== t15 || $[45] !== t18 || $[46] !== t9) {
    t19 = <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{t9}{t12}{t15}{t18}</div>;
    $[43] = t12;
    $[44] = t15;
    $[45] = t18;
    $[46] = t9;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
}